import {
  keepPreviousData,
  type QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/solid-query";
import { toast } from "solid-sonner";
import { axiosInstance } from "@/api/client";
import {
  COMPANY_KEY,
  FREELANCER_KEY,
  JOB_OFFER_KEY,
  PROFILE_KEY,
  PROPOSAL_KEY,
} from "@/api/query-keys";
import {
  parseAttachmentFilename,
  triggerBlobDownload,
} from "@/api/utils/download";
import { getApiErrorMessage } from "@/api/utils/error";
import { createPublicProfileV2QueryConfig } from "@/domains/freelance/profile/api";
import type {
  ProfileV2,
  PublicProfileV2,
} from "@/domains/freelance/profile/api/types";
import { boundSemanticSearch, boundSkillsCsv } from "@/libs/skill-input";
import type {
  ConfidenceScoreBreakdown,
  FreelancerSearchFilters,
  GetAllCompanyActiveJobOffersResponse,
  SearchFreelancersResponse,
  SendProposalRequest,
  SendProposalResponse,
  SheetOrigin,
  SmartFiltersResponse,
} from "../types";
import { stampSearchPage } from "../utils/search-id";
import {
  keepValidSubcategories,
  parseSubcategoryCsv,
} from "../utils/subcategory-filter";

const SEMANTIC_SEARCH_TIMEOUT_MS = 30_000;

// Query Keys
export const FREELANCERS_SEARCH_KEY = [FREELANCER_KEY, "search"] as const;
export const CONFIDENCE_SCORE_EXPLANATION_KEY = [
  FREELANCER_KEY,
  "confidence-score-explanation",
] as const;
// Chiave dedicata (non sotto PROFILE_KEY, vedi createFreelancerSearchProfileQueryConfig).
export const FREELANCER_SEARCH_PROFILE_KEY = [
  FREELANCER_KEY,
  "search-profile",
] as const;

const CONFIDENCE_SCORE_EXPLANATION_TIMEOUT_MS = 30_000;
const CONFIDENCE_SCORE_EXPLANATION_GC_TIME_MS = 30 * 60 * 1000;

const boundPositiveInteger = (value: number | undefined, max?: number) => {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const positive = Math.max(1, Math.floor(value));
  return max === undefined ? positive : Math.min(max, positive);
};

const getAllCompanyActiveJobOffers = async (companyId: string) => {
  const { data } =
    await axiosInstance.get<GetAllCompanyActiveJobOffersResponse>(
      `/v1/ats/${companyId}/job-offers`,
      {
        params: {
          payment_status: "completed",
          is_active: true,
        },
      },
    );
  return data;
};

export const createGetAllAtsJobOffersQueryKey = (companyId: string) => {
  return {
    queryKey: [COMPANY_KEY, JOB_OFFER_KEY, companyId],
    queryFn: () => getAllCompanyActiveJobOffers(companyId),
  };
};

// ============================================
// Freelancers Search (POST /api/freelancers/search)
// ============================================

// 1. Base fetcher function
export const searchFreelancers = async (
  filters: FreelancerSearchFilters & {
    page?: number;
    per_page?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  },
  signal?: AbortSignal,
) => {
  // `job_subcategory` leaves the body as an array, only when non-empty.
  const { job_subcategory: subcategoryCsv, ...rest } = filters;
  const subcategories = filters.job_category
    ? keepValidSubcategories(
        filters.job_category,
        parseSubcategoryCsv(subcategoryCsv),
      )
    : [];
  const { data } = await axiosInstance.post<SearchFreelancersResponse>(
    "/v1/freelancers/search",
    {
      ...rest,
      ...(subcategories.length > 0 && { job_subcategory: subcategories }),
      page: boundPositiveInteger(filters.page),
      per_page: boundPositiveInteger(filters.per_page, 100),
      search: boundSemanticSearch(filters.search),
      skills: boundSkillsCsv(filters.skills),
    },
    { signal, timeout: SEMANTIC_SEARCH_TIMEOUT_MS },
  );
  return data;
};

export type FreelancerSearchFiltersWithPagination = FreelancerSearchFilters & {
  per_page?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

// Infinite query for freelancer search. Accepts an accessor so the query key updates when filters change.
export const useInfiniteSearchFreelancers = (
  getFilters: () => FreelancerSearchFiltersWithPagination,
  getEnabled: () => boolean = () => true,
) => {
  return useInfiniteQuery(() => {
    const current = getFilters();
    return {
      queryKey: [...FREELANCERS_SEARCH_KEY, "infinite", current],
      // Ogni freelancer esce con il `search_id` della propria pagina, così i
      // consumatori a valle (flatten, sheet, selezione, salvataggio) lo
      // ereditano come dato senza alcuna lettura ambient. Stampato qui e non in
      // una `select`: O(pagina) per fetch invece di O(totale) per notifica, e
      // l'identità delle righe già a schermo resta stabile — vedi il perché in
      // `utils/search-id.ts`.
      queryFn: async ({ pageParam = 1, signal }) =>
        stampSearchPage(
          await searchFreelancers(
            {
              ...current,
              sortBy: "confidence_score",
              sortOrder: "desc",
              page: pageParam,
            },
            signal,
          ),
        ),
      initialPageParam: 1,
      getPreviousPageParam: (firstPage) => firstPage.pagination.prevPage,
      getNextPageParam: (lastPage) => lastPage.pagination.nextPage,
      placeholderData: keepPreviousData,
      retry: false,
      retryOnMount: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: getEnabled(),
    };
  });
};

export const fetchConfidenceScoreExplanation = async (
  confidenceScoreBreakdown: ConfidenceScoreBreakdown,
  signal?: AbortSignal,
) => {
  const { data } = await axiosInstance.post<{ comment: string }>(
    "/v1/freelancers/confidence-score/explanation",
    { confidence_score_breakdown: confidenceScoreBreakdown },
    { signal, timeout: CONFIDENCE_SCORE_EXPLANATION_TIMEOUT_MS },
  );

  return data.comment;
};

/**
 * Config del commento sul breakdown. `enabled` resta `false` di default:
 * il config è condiviso col tooltip della card (`ConfidenceScoreBadge`, che
 * usa `refetch()` manuale) — renderlo reattivo qui farebbe partire una POST
 * per ogni card al mount della lista. Il fetch automatico dello sheet passa
 * da `useAutoConfidenceScoreExplanation`, che opta in esplicitamente.
 * Stessa queryKey in entrambi i casi: la cache per breakdown è condivisa.
 */
export const createConfidenceScoreExplanationQueryConfig = (
  confidenceScoreBreakdown: ConfidenceScoreBreakdown,
  opts?: { enabled?: boolean },
) => ({
  queryKey: [
    ...CONFIDENCE_SCORE_EXPLANATION_KEY,
    confidenceScoreBreakdown,
  ] as const,
  queryFn: ({ signal }: QueryFunctionContext) =>
    fetchConfidenceScoreExplanation(confidenceScoreBreakdown, signal),
  enabled: opts?.enabled ?? false,
  retry: false,
  // "static" (non un numero): questa chiave vive sotto FREELANCER_KEY, e
  // `isStaleByTime` in query-core valuta staleTime === "static" PRIMA di
  // isInvalidated. Un commento AI già generato deve restare tale anche se un
  // futuro `invalidateQueries({queryKey: [FREELANCER_KEY]})` altrove nel
  // dominio la matcha per prefisso (M7): niente rigenerazioni-fantasma, niente
  // fronti di fetchStatus spuri per le macchine a stati che li osservano.
  staleTime: "static" as const,
  gcTime: CONFIDENCE_SCORE_EXPLANATION_GC_TIME_MS,
});

export const useConfidenceScoreExplanation = (
  getConfidenceScoreBreakdown: () => ConfidenceScoreBreakdown,
) =>
  useQuery(() =>
    createConfidenceScoreExplanationQueryConfig(getConfidenceScoreBreakdown()),
  );

/**
 * Variante reattiva del config per il fetch automatico dello sheet: il gate
 * vive QUI, non nel chiamante — mai una chiamata senza breakdown (E-13).
 * Senza breakdown la query resta spenta su una chiave neutra.
 */
export const createAutoConfidenceScoreExplanationQueryConfig = (
  confidenceScoreBreakdown: ConfidenceScoreBreakdown | undefined,
  active: boolean,
) =>
  confidenceScoreBreakdown
    ? createConfidenceScoreExplanationQueryConfig(confidenceScoreBreakdown, {
        enabled: active,
      })
    : {
        queryKey: [...CONFIDENCE_SCORE_EXPLANATION_KEY, "disabled"] as const,
        queryFn: async () => "",
        enabled: false,
        retry: false,
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: CONFIDENCE_SCORE_EXPLANATION_GC_TIME_MS,
      };

/**
 * Fetch automatico del commento quando lo sheet mostra il profilo (AC17):
 * parte solo con `getActive()` vero E un breakdown presente. Cache identica
 * al tooltip (stessa queryKey per breakdown): un commento già generato non
 * viene rigenerato.
 */
export const useAutoConfidenceScoreExplanation = (
  getConfidenceScoreBreakdown: () => ConfidenceScoreBreakdown | undefined,
  getActive: () => boolean,
) =>
  useQuery(() =>
    createAutoConfidenceScoreExplanationQueryConfig(
      getConfidenceScoreBreakdown(),
      getActive(),
    ),
  );

// ============================================
// Freelancer profile from search (GET /v1/freelancers/:freelancerId/profile)
// ============================================

export type FreelancerSearchProfileParams = {
  searchId?: string;
  jobOfferId?: string;
};

// 1. Base fetcher function. Endpoint publisher-gated che, a differenza di
// `GET /profile/:profileId`, accetta il `search_id` e registra la
// `profile_view` come esito della ricerca. I parametri vengono aggiunti solo
// se definiti: una chiave `undefined` non deve mai finire in query.
export const getFreelancerSearchProfile = async (
  freelancerId: string,
  params?: FreelancerSearchProfileParams,
) => {
  const { data } = await axiosInstance.get<{ profile: ProfileV2 }>(
    `/v1/freelancers/${freelancerId}/profile`,
    {
      params: {
        ...(params?.searchId ? { search_id: params.searchId } : {}),
        ...(params?.jobOfferId ? { job_offer_id: params.jobOfferId } : {}),
      },
    },
  );
  return data.profile;
};

// 2. Query config creator. La chiave include i parametri: una nuova ricerca
// (nuovo `search_id`) è una entry di cache nuova ⇒ GET fresca ⇒ la
// `profile_view` viene registrata per la nuova impression; riaprire lo stesso
// profilo dentro la stessa ricerca serve da cache, senza doppio log.
// Chiave DEDICATA (`FREELANCER_SEARCH_PROFILE_KEY`), non sotto `PROFILE_KEY`:
// quel prefisso è invalidato ampiamente (`useSendProposal` e altri) per
// rinfrescare dati che NON includono questo fetch — `already_applied`
// sullo sheet viene dalla riga di ricerca (`props.freelancer.already_applied`),
// mai da questo profilo. Restarne fuori evita una GET (e un log `profile_view`)
// duplicati ogni volta che una di quelle invalidazioni scatta mentre lo sheet è
// aperto — l'unico gate reale resta `staleTime` sotto, sulla stessa entry.
export const createFreelancerSearchProfileQueryConfig = (
  freelancerId: string,
  params?: FreelancerSearchProfileParams,
) => ({
  queryKey: [
    ...FREELANCER_SEARCH_PROFILE_KEY,
    freelancerId,
    params?.searchId ?? null,
    params?.jobOfferId ?? null,
  ],
  queryFn: () => getFreelancerSearchProfile(freelancerId, params),
  enabled: !!freelancerId,
});

/**
 * La sheet dei risultati è condivisa fra i due tab: la scelta dell'endpoint
 * segue la PROVENIENZA dell'apertura, non uno stato ambient.
 *
 * - `search`: endpoint della ricerca, con `search_id`/`job_offer_id` in query.
 * - `pool`: vecchio endpoint pubblico, invariato. Non è un dettaglio di
 *   telemetria: il nuovo endpoint filtra per `discoverableFreelancerClauses()`
 *   e risponderebbe 404 sui profili pool non-discoverable che oggi la sheet
 *   apre in sola lettura.
 *
 * Chiavi disgiunte per i due rami ⇒ nessuna entry di cache condivisa fra
 * payload diversi.
 *
 * `active` è il gate di apertura, e non è un'ottimizzazione: il route conserva
 * la selezione dopo la chiusura (serve a Kobalte per giocare l'animazione di
 * uscita), quindi la sheet resta MONTATA e chiusa con una query viva. Col
 * vecchio endpoint era innocuo; il nuovo registra una `profile_view`, così
 * qualunque cambio di chiave a sheet chiuso — per esempio il ritorno al tab
 * Ricerca con ancora in memoria una selezione arrivata dal pool, che non porta
 * `search_id` — inventerebbe un'apertura che il recruiter non ha mai fatto,
 * e senza attribuzione. Una `profile_view` si registra quando la sheet è
 * davvero aperta.
 *
 * `staleTime` è la METÀ NECESSARIA di quel gate. Il `QueryClient` del progetto
 * non imposta `staleTime` (⇒ 0 ⇒ sempre stale), e un `enabled` che torna
 * `true` è un fronte che `shouldFetchOptionally` rifetcha: senza `staleTime`
 * ogni riapertura della stessa sheet sarebbe una GET nuova, quindi una
 * `profile_view` duplicata per la stessa impression — l'opposto di quanto
 * promette il commento del creator sopra. Misurato: 3 aperture dello stesso
 * profilo ⇒ 3 GET senza, 1 GET con. La granularità dell'attribuzione non
 * cambia: la chiave contiene già il `search_id`, quindi una ricerca nuova resta
 * una entry nuova ⇒ una GET (e un log) nuovi.
 */
export type SheetProfileArgs = {
  origin: SheetOrigin;
  freelancerId: string;
  params?: FreelancerSearchProfileParams;
  /** Sheet davvero aperta. Vedi sopra: il ramo `search` scrive. */
  active: boolean;
};

/** Una impression = una GET. Riaperture dentro la finestra: dalla cache. */
const SHEET_PROFILE_STALE_TIME_MS = 5 * 60 * 1000;

export const createSheetProfileQueryConfig = ({
  origin,
  freelancerId,
  params,
  active,
}: SheetProfileArgs): {
  queryKey: (string | null)[];
  // La vista pubblica è il **minimo comune denominatore** dei due rami: quello
  // di ricerca risponde ancora con la riga intera (rotta company-facing non
  // toccata dalla spec) ed è assegnabile, quello pubblico no. Annotare
  // `ProfileV2` qui rimetterebbe nel contratto della sheet i campi che
  // `/profile/:id` non manda più.
  queryFn: () => Promise<PublicProfileV2>;
  enabled: boolean;
  staleTime: number;
} => {
  const config =
    origin === "search"
      ? createFreelancerSearchProfileQueryConfig(freelancerId, params)
      : createPublicProfileV2QueryConfig(freelancerId);
  return {
    ...config,
    enabled: active && config.enabled,
    // Solo qui, non in `createPublicProfileV2QueryConfig`: quel config ha altri
    // consumatori, che non hanno il gate di apertura e non devono cambiare.
    staleTime: SHEET_PROFILE_STALE_TIME_MS,
  };
};

/**
 * Un solo accessor per tutti gli argomenti: la sheet resta montata mentre gioca
 * l'uscita e segue il freelancer selezionato senza rimontarsi, quindi origine,
 * id, parametri e stato di apertura devono poter cambiare sotto la stessa query
 * (il thunk di `useQuery` li rilegge per intero a ogni notifica).
 * I dati già in cache restano leggibili a sheet chiusa (`enabled: false` non
 * svuota `data`), quindi l'uscita si gioca sul profilo ancora a schermo.
 */
export const useSheetProfile = (args: () => SheetProfileArgs) =>
  useQuery(() => createSheetProfileQueryConfig(args()));

// ============================================
// Send Proposal (POST /api/proposals/send)
// ============================================

// 1. Base mutator function
export const sendProposal = async (input: SendProposalRequest) => {
  const { data } = await axiosInstance.post<SendProposalResponse>(
    "/v1/proposals/send",
    input,
  );
  return data;
};

// 2. Custom hook
export const useSendProposal = () => {
  const queryClient = useQueryClient();

  return useMutation(() => ({
    mutationFn: (input: SendProposalRequest) => sendProposal(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROPOSAL_KEY] });
      // Solo la lista di ricerca, non l'intero prefisso FREELANCER_KEY: serve a
      // rinfrescare `already_applied` sulle card dopo l'invio (FreelanceCard,
      // freelance-sheet). Un `[FREELANCER_KEY]` nudo qui matcherebbe per
      // prefisso anche CONFIDENCE_SCORE_EXPLANATION_KEY, azzerando la cache
      // del commento AI a ogni proposta inviata (M7, AC17).
      queryClient.invalidateQueries({ queryKey: FREELANCERS_SEARCH_KEY });
      queryClient.invalidateQueries({ queryKey: [JOB_OFFER_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Invio proposta non riuscito"));
    },
  }));
};

// ============================================
// Smart Filters (POST /v1/freelancers/smart-filters)
// ============================================

export const postSmartFilters = async (text: string) => {
  const { data } = await axiosInstance.post<SmartFiltersResponse>(
    "/v1/freelancers/smart-filters",
    { text },
  );
  return data;
};

export const useSmartFilters = () => {
  return useMutation(() => ({
    mutationFn: (text: string) => postSmartFilters(text),
    onError: (error: unknown) => {
      toast.error(
        getApiErrorMessage(error, "Errore nell'analisi dei filtri smart"),
      );
    },
  }));
};

// ============================================
// Anonymous CV (GET /v1/freelancers/{id}/anonymous-cv)
// ============================================

export type AnonymousCvOptions = {
  displayName?: string;
};

// 1. Base fetcher: returns the branded anonymized CV as a PDF blob. Single-mode
// (deterministic) anonymization — no level/rate options. Cache-backed server-side.
export const downloadAnonymousCv = async (
  freelancerId: string,
  opts: AnonymousCvOptions = {},
) => {
  const response = await axiosInstance.get(
    `/v1/freelancers/${freelancerId}/anonymous-cv`,
    {
      params: {
        ...(opts.displayName ? { display_name: opts.displayName } : {}),
      },
      responseType: "blob",
    },
  );

  const filename = parseAttachmentFilename(
    response.headers["content-disposition"] as string | undefined,
    "cv-anonimo.pdf",
  );

  return { blob: response.data as Blob, filename };
};

// 2. Custom hook: downloads and saves the PDF, with toast feedback.
export const useDownloadAnonymousCv = () => {
  return useMutation(() => ({
    mutationFn: (input: { freelancerId: string } & AnonymousCvOptions) => {
      const { freelancerId, ...opts } = input;
      return downloadAnonymousCv(freelancerId, opts);
    },
    onSuccess: ({ blob, filename }) => {
      triggerBlobDownload(blob, filename);
      toast.success("CV anonimo generato");
    },
    onError: (error: unknown) => {
      toast.error(
        getApiErrorMessage(error, "Generazione CV anonimo non riuscita"),
      );
    },
  }));
};

// ============================================
// Anonymous CV bundle (POST /v1/freelancers/anonymous-cv-bundle) — ZIP of PDFs
// ============================================

// Request body: 1..20 freelancer PROFILE ids, all discoverable in the active
// search (the BE authorizes on the same discoverability guard as the single CV).
// The response is a ZIP with one anonymized branded PDF per candidate.
export type AnonymousCvBundleRequest = {
  freelancer_ids: string[];
};

// 1. Base fetcher: POSTs the selection and returns the ZIP as a blob.
export const downloadAnonymousCvBundle = async (
  input: AnonymousCvBundleRequest,
) => {
  const response = await axiosInstance.post(
    "/v1/freelancers/anonymous-cv-bundle",
    input,
    { responseType: "blob" },
  );

  const filename = parseAttachmentFilename(
    response.headers["content-disposition"] as string | undefined,
    "cv-anonimi.zip",
  );

  return { blob: response.data as Blob, filename };
};

// 2. Custom hook: downloads and saves the ZIP, with toast feedback.
export const useDownloadAnonymousCvBundle = () => {
  return useMutation(() => ({
    mutationFn: (input: AnonymousCvBundleRequest) =>
      downloadAnonymousCvBundle(input),
    onSuccess: ({ blob, filename }: { blob: Blob; filename: string }) => {
      triggerBlobDownload(blob, filename);
      toast.success("CV anonimi generati");
    },
    onError: (error: unknown) => {
      toast.error(
        getApiErrorMessage(error, "Generazione dei CV anonimi non riuscita"),
      );
    },
  }));
};
